import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { TextInput, useToast } from "../../../components";
import { Spacer } from "../../spacer";
import { PortalButton } from "./portal_button";
import { PortalModal } from "./portal_modal";
import styles from "./portal_cancel_membership.module.css";
import { PortalPathParams } from "../hooks/use_portal_history";
import { useParams } from "react-router-dom";
import { useUpcomingPaymentQuery } from "../hooks/use_upcoming_payment_query";
import { formatCurrency } from "@/lib/currency/currency";
import { formatDate } from "@/offering/date_utils";
import { isAfter, parseISO } from "date-fns";
import {
  CancelMembershipReasonCode,
  useCancelSubscription,
} from "../hooks/use_cancel_subscription";
import { PulseLoader } from "@/components/pulse_loader";
import { useSubscriptionDetailsQuery } from "../hooks/use_subscription_details_query";
import { useSubscriptionPolicyQuery } from "../hooks/use_subscription_policy_query";
import { useMembershipManagementAnalytics } from "../hooks/use_membership_management_analytics";
import { getSystemLocale } from "@/lib/locale/locale";

const initialReasonState: {
  label: string;
  checked: boolean;
  code: CancelMembershipReasonCode;
}[] = [
  {
    label: "Not saving enough",
    checked: false,
    code: "not_saving_enough",
  },
  {
    label: "Want to take a break",
    checked: false,
    code: "want_to_take_a_break",
  },
  {
    label: "Issues with my membership",
    checked: false,
    code: "issue_with_membership",
  },
  {
    label: "Something else",
    checked: false,
    code: "something_else",
  },
];

interface PortalCancelMembershipProps {
  partnerId: string;
}

export function PortalCancelMembership({
  partnerId,
}: PortalCancelMembershipProps) {
  const [openDialog, setOpenDialog] = useState(false);
  const [reasons, setReasons] = useState(initialReasonState);
  const [addtionalReasonText, setAdditionalReasonText] = useState<string>();
  const beginningStep = 0;
  const [currentStep, setCurrentStep] = useState(beginningStep);
  const [loadingCancel, setLoadingCancel] = useState(false);

  const toast = useToast();
  const { subscriptionId } = useParams<PortalPathParams>();

  const analyticEvent = useMembershipManagementAnalytics();
  const { data: upcomingPaymentData } = useUpcomingPaymentQuery(
    partnerId,
    subscriptionId
  );
  const {
    data: subscriptionDetailsData,
    revalidate: revalidateSubscriptionDetails,
  } = useSubscriptionDetailsQuery(partnerId, subscriptionId);
  const { data: policyData } = useSubscriptionPolicyQuery(
    subscriptionDetailsData?.membership.membershipPlanId
  );
  const cancelSubscription = useCancelSubscription();

  const isSubscriptionActive = subscriptionDetailsData?.status === "active";

  const formatedEndDate = !!subscriptionDetailsData?.endDateIfCancelNow
    ? formatDate(subscriptionDetailsData.endDateIfCancelNow)
    : "";

  const hasNextBill =
    !!upcomingPaymentData?.paymentDate &&
    !!subscriptionDetailsData?.endDateIfCancelNow &&
    isAfter(
      parseISO(subscriptionDetailsData.endDateIfCancelNow),
      parseISO(upcomingPaymentData.paymentDate)
    );

  const gracePeriodMonths = policyData?.cancellationGracePeriodMonths;

  const handleSelectReason = (index: number) => {
    setReasons(() => {
      const updated = initialReasonState.map((r) => ({
        ...r,
        checked: false,
      }));
      updated[index].checked = true;
      return updated;
    });
  };

  const handleCancelMembership = async () => {
    try {
      const reasonCode = reasons.find((r) => r.checked)?.code;
      if (reasonCode) {
        setLoadingCancel(true);
        await cancelSubscription(partnerId, subscriptionId, {
          code: reasonCode,
          addition: addtionalReasonText?.trim(),
        });
        analyticEvent("Cancel Membership Confirmation");
        setCurrentStep((state) => state + 1);
        revalidateSubscriptionDetails();
      }
    } catch (error) {
      toast.error({ message: "Error happened" });
    }
    setLoadingCancel(false);
  };

  const handleCloseDialog = () => {
    setReasons(initialReasonState);
    setOpenDialog(false);
    setCurrentStep(beginningStep);
  };

  const handleClickCancel = () => {
    setOpenDialog(true);
    analyticEvent("Cancel Membership Click");
  };

  const handleContinueToConfirmStep = () => {
    setCurrentStep(1);
    analyticEvent("Cancel Membership Survey Continue", {
      "Browser date time": new Date().toISOString(),
      "Browser locale": getSystemLocale(),
      "End date to show": formatedEndDate,
    });
  };

  const steps = [
    {
      body: (
        <div>
          <div className={styles.title}>We're sorry to see you go!</div>
          <Spacer size={16} />
          <div>
            To help us improve, please let us know why you want to cancel your
            membership:
          </div>
          <Spacer size={16} />
          <div>
            {reasons.map((r, i) => (
              <div key={i} className={styles.reasonLabelWrapper}>
                <Form.Check
                  type="radio"
                  checked={r.checked}
                  label={r.label}
                  onClick={() => handleSelectReason(i)}
                />
              </div>
            ))}
          </div>
          <Spacer size={16} />
          <div>Anything else you’d like to add? (Optional)</div>
          <Spacer size={8} />
          <TextInput
            value={addtionalReasonText}
            onChange={setAdditionalReasonText}
          />
        </div>
      ),
      footer: (
        <div className={styles.btnWrapper}>
          <PortalButton
            onClick={handleContinueToConfirmStep}
            disabled={!reasons.some((r) => r.checked)}
          >
            Continue
          </PortalButton>
        </div>
      ),
    },
    {
      body: (
        <div>
          <div className={styles.cancelTitle}>
            Are you sure you want to cancel?
          </div>
          {upcomingPaymentData && (
            <p className="my-4">
              {hasNextBill ? (
                gracePeriodMonths && gracePeriodMonths > 1 ? (
                  <>
                    You will continue to be billed monthly until your membership
                    ends on <b>{formatedEndDate}</b>. Your next payment of{" "}
                    <b>
                      {formatCurrency(
                        upcomingPaymentData.totalPrice,
                        upcomingPaymentData.currency
                      )}
                    </b>{" "}
                    will be processed on{" "}
                    <b>{formatDate(upcomingPaymentData.paymentDate)}</b>.
                  </>
                ) : (
                  <>
                    Please note that your next payment of{" "}
                    <b>
                      {formatCurrency(
                        upcomingPaymentData.totalPrice,
                        upcomingPaymentData.currency
                      )}
                    </b>{" "}
                    will be processed on{" "}
                    <b>{formatDate(upcomingPaymentData.paymentDate)}</b>, and
                    access to your membership will end on{" "}
                    <b>{formatedEndDate}</b>
                  </>
                )
              ) : (
                <>
                  Access to your membership will end on <b>{formatedEndDate}</b>
                </>
              )}
            </p>
          )}
          {policyData?.cancellationText && (
            <div
              dangerouslySetInnerHTML={{
                __html: policyData.cancellationText,
              }}
            ></div>
          )}
        </div>
      ),
      footer: (
        <div className={styles.btnWrapper}>
          {loadingCancel ? (
            <PulseLoader />
          ) : (
            <>
              <PortalButton
                appearance="danger"
                onClick={handleCancelMembership}
              >
                Cancel membership
              </PortalButton>
              <PortalButton appearance="text" onClick={handleCloseDialog}>
                Not right now
              </PortalButton>
            </>
          )}
        </div>
      ),
    },
    {
      body: (
        <div>
          <div className={styles.cancelTitle}>We’re sorry to see you go!</div>
          <p className="my-4">
            Your membership cancellation has been processed. You will receive an
            email shortly confirming your cancellation. Please note that access
            to your membership ends on <b>{formatedEndDate}</b>.
          </p>
          <p className="my-4">
            Remember, the door is always open for your return, and we’d be
            thrilled to welcome you back anytime. See you around!
          </p>
          {policyData?.cancellationText && (
            <div
              dangerouslySetInnerHTML={{
                __html: policyData.cancellationText,
              }}
            ></div>
          )}
        </div>
      ),
      footer: (
        <div className={styles.btnWrapper}>
          <PortalButton onClick={() => setOpenDialog(false)}>Done</PortalButton>
        </div>
      ),
    },
  ];

  return (
    <div>
      {isSubscriptionActive && policyData?.allowSelfCancellation && (
        <PortalButton appearance="text" onClick={handleClickCancel}>
          Cancel your membership
        </PortalButton>
      )}
      <PortalModal
        title="Cancel Membership"
        show={openDialog}
        onHide={handleCloseDialog}
        body={steps[currentStep].body}
        footer={steps[currentStep].footer}
      />
    </div>
  );
}
